const BASE_URL = process.env.REACT_APP_API_URL ?? '';

export function getMachineID(): string {
  const params = new URLSearchParams(window.location.search);
  const fromURL = params.get('machine');
  if (fromURL) {
    sessionStorage.setItem('rush_machine_id', fromURL);
    return fromURL;
  }
  return sessionStorage.getItem('rush_machine_id') ?? '';
}

/** One ingredient of a 'mixed' preset recipe: a standalone component product
 *  and its literal pour amount (ml). Present on the machine /products
 *  response for 'mixed' products only. */
export type MachineProductComponent = {
  component_product_id: string;
  name: string;
  component_ml: number;
};

export type MachineProduct = {
  product_id: string;
  name: string;
  alcohol_percentage?: number;
  /** Whether this drink contains alcohol. Prefer this over inferring from
   *  alcohol_percentage when the backend sends it. */
  is_alcoholic?: boolean;
  /** Optional guest-facing blurb for the drink (mixed presets or standalones). */
  description?: string | null;
  price_cents?: number;
  enabled_cup_sizes?: string[];
  is_enabled: boolean;
  image_url?: string | null;
  quantity_ml: number;
  /** 'standalone' (a base liquid — a Custom Mix ingredient) or 'mixed' (a
   *  preset recipe built from standalone components). Optional: only a backend
   *  that supports the field sends it. */
  drink_category?: string;
  /** For 'mixed' products: the recipe. Absent/empty for standalone products. */
  components?: MachineProductComponent[];
  /** The physical pipe (1-based, S1…S7) this standalone product is assigned to
   *  on this machine. Absent when unassigned (or for 'mixed' presets). A base
   *  liquid without a pipe cannot be dispensed. */
  pipe_number?: number | null;
  /** Most ml of this product a single order may pour. Informational only in
   *  this app (it never builds a pour) — omitted means no limit. */
  max_pour_ml?: number;
};

/** Resolve alcoholic flag: prefer `is_alcoholic`, else fall back to percentage. */
export function productIsAlcoholic(p: MachineProduct): boolean | undefined {
  const raw = p.is_alcoholic as unknown;
  if (typeof raw === 'boolean') return raw;
  if (raw === 'true' || raw === 1 || raw === '1') return true;
  if (raw === 'false' || raw === 0 || raw === '0') return false;
  if (typeof p.alcohol_percentage === 'number') return p.alcohol_percentage > 0;
  return undefined;
}

/** A standalone product can only be dispensed when it sits on a pipe. */
export const isOnPipe = (p: MachineProduct): boolean => p.pipe_number != null;

export const isMixedProduct = (p: MachineProduct): boolean => p.drink_category === 'mixed';

/**
 * Whether a 'mixed' preset can actually be poured right now. It must carry a
 * recipe (a mixed product without components cannot be poured at all), and
 * every recipe component must resolve to an enabled standalone product that is
 * assigned to a pipe and has enough stock to cover its literal component_ml
 * pour amount.
 */
export function isMixedPourable(
  product: MachineProduct,
  catalog: Map<string, MachineProduct>,
): boolean {
  const components = product.components ?? [];
  if (components.length === 0) return false;
  return components.every((c) => {
    const ingredient = catalog.get(c.component_product_id);
    if (!ingredient || !ingredient.is_enabled || !isOnPipe(ingredient)) return false;
    return ingredient.quantity_ml >= c.component_ml;
  });
}

type ProductsResponse = {
  products: MachineProduct[];
  config_version: number;
};

export async function fetchMachineProducts(): Promise<MachineProduct[]> {
  const machineID = getMachineID();
  if (!BASE_URL || !machineID) throw new Error('No machine ID — scan the machine QR code to start ordering');
  const res = await fetch(`${BASE_URL}/api/v1/machines/${machineID}/products`);
  if (!res.ok) throw new Error(`Products fetch failed: ${res.status}`);
  const data: ProductsResponse = await res.json();
  return data.products;
}

// ── Post-pour guest survey ───────────────────────────────────────────────────

export type SubmitSurveyRequest = {
  product_id: string;
  rating: number;            // 1–5
  service_rating: number;    // 1–5
  would_recommend: boolean;  // required
  age: number;               // 1–120
  nationality: string;       // max 100
  want_more_tech: boolean;   // required
  comment?: string;
  improvement_suggestion?: string;
};

export type SubmitSurveyResponse = {
  response_id: string;
  machine_id: string;
  product_id: string;
  rating: number;
  service_rating: number;
  would_recommend: boolean;
  age: number;
  nationality: string;
  want_more_tech: boolean;
  comment?: string;
  improvement_suggestion?: string;
  submitted_at: string; // RFC-3339
};

/**
 * Submit a post-pour survey. Soft-fail friendly — callers should not block the
 * guest on failure. Auth is not required (same as guest menu routes).
 */
export async function submitSurvey(
  body: SubmitSurveyRequest,
): Promise<SubmitSurveyResponse> {
  const machineID = getMachineID();
  if (!BASE_URL || !machineID) throw new Error('No machine ID — cannot submit survey');
  const res = await fetch(`${BASE_URL}/api/v1/machines/${machineID}/surveys`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const err = await res.json();
      if (err?.description) detail = err.description;
      else if (err?.status) detail = err.status;
    } catch { /* ignore */ }
    throw new Error(`Survey submit failed: ${detail}`);
  }
  return res.json();
}
